<template>
  <div>
    <SubHeaderItem class="actualites-page-title-content">
      <p class="actualites-page-title" v-html="$t('actualitesPage.encart-titre')"></p>
      <p class="actualites-page-texte" v-html="$t('actualitesPage.encart-texte')"></p>
    </SubHeaderItem>

    <PageView :main-title="$t('graePage.actualites.title')" :no-sub-title-expected="true">
      <ContentView class="actualite-page">
        <div class="actualites-page-content">
          <div class="actualites-page-columns">
            <div class="actualites-page-column">
              <ActualitesCardsView
                :is-loading-data="isLoadingData"
                :title="$t('actualitesPage.activites-national')"
                :actualites="actualitesNat"
              />
            </div>
            <div class="actualites-page-column">
              <ActualitesCardsView
                :is-loading-data="isLoadingData"
                :title="$t('actualitesPage.activites-regional')"
                :actualites="actualitesReg"
              />
            </div>
          </div>

          <div class="actualites-page-contact">
            <ContactCardsView :contact="contact" />
          </div>
        </div>
      </ContentView>
    </PageView>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import {
  ActualiteDTO,
  FilterDTO,
  GraeDTO,
  PaginationParameter,
  UtilisateurDTO,
} from "@/model/bean/GeneratedDTOs"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { OrganismeService } from "@/services/rest/organisme/OrganismeService"
import { UsersService } from "@/services/rest/users/UsersService"
import { ActualiteService } from "@/services/rest/actualite/ActualiteService"
import ConversionUtils from "@/services/ConversionUtils"
import PageView from "@/views/commons/PageView.vue"
import ContentView from "@/views/commons/ContentView.vue"
import { LoginService } from "@/services/rest/login/LoginService"
import ContactCardsView from "@/views/commons/ContactCardsView.vue"
import ActualitesCardsView from "@/pages/actualite/ActualitesCardsView.vue"
import { Filters } from "@/model/bean/Filters"
import SubHeaderItem from "@/views/commons/SubHeaderItem.vue"

export default defineComponent({
  name: "ActualitePage",
  components: {
    SubHeaderItem,
    ActualitesCardsView,
    ContactCardsView,
    PageView,
    ContentView,
  },
  data() {
    return {
      loginService: LoginService.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,
      organismeService: OrganismeService.INSTANCE,
      usersService: UsersService.INSTANCE,
      actualiteService: ActualiteService.INSTANCE,
      loggedUser: new UtilisateurDTO(),
      isLoadingData: true,
      grae: new GraeDTO(),
      contact: new UtilisateurDTO(),
      actualitesNat: [] as ActualiteDTO[],
      actualitesReg: [] as ActualiteDTO[],
    }
  },
  created(): void {
    const loggedUser = this.loginService.getLoggedUser()
    if (!loggedUser) {
      return
    }
    this.loggedUser = loggedUser
    this.init()
  },
  methods: {
    async init(): Promise<void> {
      this.isLoadingData = true

      await this.setGrae()
      await this.setContact()
      await this.setActualites()

      this.isLoadingData = false
    },

    async setGrae(): Promise<void> {
      if (this.loggedUser && this.loggedUser.grae && this.loggedUser.grae.id) {
        this.grae = await this.organismeService.getGrae(this.loggedUser.grae.id)
      }
    },

    async setContact(): Promise<void> {
      if (this.grae?.contactId) {
        try {
          this.contact = await this.usersService.getUserProfile(this.grae.contactId)
        } catch (e: any) {
          this.infoReportingService.error("Erreur rencontrée sur le contact", e as Error)
        }
      }
    },

    async setActualites(): Promise<void> {
      const paginationParameter = new PaginationParameter()
      paginationParameter.pageNumber = 1
      paginationParameter.pageSize = 9999

      const filters = new Array<FilterDTO>()
      const today = ConversionUtils.convertToUTCDate(new Date()).toISOString().substring(0, 10)
      const publishedAtFilter = Filters.asFilter({ property: "is-published-at", values: [today] })
      filters.push(publishedAtFilter)

      try {
        const paginatedResponse = await this.actualiteService.getAllActualitesForGRAE(
          this.grae?.id,
          paginationParameter,
          filters,
        )

        if (paginatedResponse.elements.length > 0) {
          const { withGraeId, withoutGraeId } = paginatedResponse.elements.reduce(
            (acc, actu: ActualiteDTO) => {
              if (!actu.grae) {
                acc.withoutGraeId.push(actu)
              } else {
                acc.withGraeId.push(actu)
              }
              return acc
            },
            { withGraeId: [] as ActualiteDTO[], withoutGraeId: [] as ActualiteDTO[] },
          )

          this.actualitesNat = withoutGraeId
          this.actualitesReg = withGraeId
        }
      } catch (e: any) {
        this.infoReportingService.error(
          this.$t("projet.en-bref.error-actualites").toString(),
          e as Error,
        )
      }
    },
  },
})
</script>

<style lang="scss">
.actualites-page-title-content {
  text-align: center;
  margin: 2px 0 0 0;

  .SubHeaderItem {
    position: relative;
    height: auto;
    box-shadow:
      inset 0 1px 0 0 #333,
      inset 0 -1px 0 0 #333;
    border-radius: 0;

    .SubHeaderItem-content {
      width: 100%;
    }
  }

  p.actualites-page-title {
    font-weight: bold;
    font-size: 2rem;
    padding-bottom: 1rem;
  }

  p.actualites-page-texte {
    font-size: 1em;
    line-height: 3rem;
    padding-bottom: 3rem;
  }
}

.actualites-page-content {
  display: flex;
  margin-bottom: 2rem;

  .actualites-page-columns {
    flex-grow: 2;
    display: flex;
    margin-top: 18px;

    .actualites-page-column {
      flex: 1;
      margin-right: 1rem;

      .h3-title-view {
        font-weight: bold;
      }
    }
  }

  .actualites-page-contact {
    margin-top: 38px;
  }
}
</style>
